import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue, useSetRecoilState } from 'recoil';

import { adminUpdateUser, adminUserListByRoles } from '@shared/api/user/user.controller';
import { User } from '@shared/api/user/user.interface';
import { Button } from '@shared/components/Button';
import { LoadingModal } from '@shared/components/LoadingModal';
import Modal from '@shared/components/Modal';
import { TextField } from '@shared/components/TextField';
import {
  notificateError,
  notificateSuccess,
} from '@shared/plugIn/ant-notification/ant-notifiaction';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { notificationInstanceAtom } from '@shared/state/atom/notification.atom';
import { UserRole } from '@shared/types';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useAsyncFn } from 'react-use';

import {
  ADMIN_USER_CREATE_MODAL_OPEN_ID,
  AdminUserCreateModal,
} from '../components/AdminUserCreateModal';
import { useAdminTitleHook } from '../hooks/useAdminTitleHook';

const MANAGEABLE_ROLES: UserRole[] = [
  UserRole.Admin,
  UserRole.StoreSeller,
  UserRole.StoreSellerAdmin,
  UserRole.StoreSellerWorker,
  UserRole.GiftAgent,
];

const EDIT_MODAL_OPEN_ID = 'admin-user-manage-edit';

interface EditFormInputs {
  password?: string;
  name?: string;
  phoneNumber?: string;
}

interface EditModalProps {
  user: User | null;
  onSaved: () => void;
}

const AdminUserEditModal: FC<EditModalProps> = ({ user, onSaved }) => {
  const notiInstance = useRecoilValue(notificationInstanceAtom);
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(EDIT_MODAL_OPEN_ID));

  const { register, handleSubmit, reset } = useForm<EditFormInputs>({
    defaultValues: {
      password: '',
      name: user?.info?.name ?? '',
      phoneNumber: user?.info?.phoneNumber ?? '',
    },
  });

  // user 변경 시 form 동기화
  useEffect(() => {
    reset({
      password: '',
      name: user?.info?.name ?? '',
      phoneNumber: user?.info?.phoneNumber ?? '',
    });
  }, [user, reset]);

  const [{ loading }, submit] = useAsyncFn(
    async (data: EditFormInputs) => {
      if (!user) return;
      try {
        await adminUpdateUser({
          _id: user._id,
          ...(data.password ? { password: data.password } : {}),
          info: {
            ...(user.info ?? {}),
            name: data.name ?? user.info?.name,
            phoneNumber: data.phoneNumber ?? user.info?.phoneNumber,
          },
        });
        notificateSuccess(notiInstance, '저장 완료');
        setOpen(false);
        onSaved();
      } catch (err) {
        notificateError(notiInstance, '저장 실패');
        console.error(err);
      }
    },
    [user, notiInstance, onSaved, setOpen],
  );

  const onSubmit: SubmitHandler<EditFormInputs> = (data) => {
    submit(data);
  };

  return (
    <Modal openId={EDIT_MODAL_OPEN_ID}>
      <form className="space-y-4 pt-2" onSubmit={handleSubmit(onSubmit)}>
        <h2 className="text-16 font-bold">계정 수정</h2>
        <div className="grid grid-cols-1 gap-3">
          <div className="flex items-center gap-2 text-14">
            <span className="font-semibold">아이디</span>
            <span>{user?.username}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-14">
            <span className="font-semibold">권한</span>
            <span>{user?.roles?.join(', ')}</span>
          </div>
          <TextField
            {...register('name')}
            label="이름"
            placeholder="이름"
            className="w-full"
          />
          <TextField
            {...register('phoneNumber')}
            label="전화번호"
            placeholder="전화번호"
            className="w-full"
          />
          <TextField
            {...register('password')}
            label="새 비밀번호 (변경 시에만 입력)"
            placeholder="비밀번호"
            type="password"
            autoComplete="new-password"
            className="w-full"
          />
        </div>
        <div className="pt-2">
          <Button
            disabled={loading}
            type="submit"
            className="button-rectangle h-10 w-full rounded-md theme-text-3 theme-bg-main"
          >
            저장
          </Button>
        </div>
      </form>
      <LoadingModal open={loading} title="저장중" />
    </Modal>
  );
};

const AdminUserManagePage: FC = function AdminUserManagePage() {
  useAdminTitleHook('계정 관리');

  const setEditOpen = useSetRecoilState(simpleOpenAtomFamily(EDIT_MODAL_OPEN_ID));
  const setCreateOpen = useSetRecoilState(
    simpleOpenAtomFamily(ADMIN_USER_CREATE_MODAL_OPEN_ID),
  );
  const [selectedRole, setSelectedRole] = useState<UserRole | 'ALL'>('ALL');
  const [editTarget, setEditTarget] = useState<User | null>(null);

  const [{ loading, value: userList }, fetchUsers] = useAsyncFn(async () => {
    const roles =
      selectedRole === 'ALL' ? MANAGEABLE_ROLES : [selectedRole as UserRole];
    return adminUserListByRoles(roles);
  }, [selectedRole]);

  // 최초 + role 변경 시 자동 로드
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const filteredUsers = useMemo(() => {
    if (!userList) return [];
    return userList.filter((u) =>
      (u.roles ?? []).some((r) => MANAGEABLE_ROLES.includes(r)),
    );
  }, [userList]);

  const openEdit = useCallback(
    (user: User) => {
      setEditTarget(user);
      setEditOpen(true);
    },
    [setEditOpen],
  );

  return (
    <div className="space-y-4 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <label className="text-14 font-semibold">권한 필터:</label>
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value as UserRole | 'ALL')}
            className="rounded border px-2 py-1 text-14"
          >
            <option value="ALL">전체</option>
            {MANAGEABLE_ROLES.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={() => fetchUsers()}
            className="rounded-md border px-3 py-1 text-14 theme-bg-1"
          >
            새로고침
          </Button>
          <Button
            onClick={() => setCreateOpen(true)}
            className="rounded-md px-3 py-1 text-14 theme-text-3 theme-bg-main"
          >
            계정 생성
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px] table-auto border-collapse text-14">
          <thead>
            <tr className="border-b bg-gray-50">
              <th className="px-2 py-2 text-left">아이디</th>
              <th className="px-2 py-2 text-left">이름</th>
              <th className="px-2 py-2 text-left">전화번호</th>
              <th className="px-2 py-2 text-left">권한</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-2 py-4 text-center text-gray-500">
                  로딩 중...
                </td>
              </tr>
            )}
            {!loading && filteredUsers.length === 0 && (
              <tr>
                <td colSpan={5} className="px-2 py-4 text-center text-gray-500">
                  표시할 계정이 없습니다.
                </td>
              </tr>
            )}
            {!loading &&
              filteredUsers.map((user) => (
                <tr key={user._id} className="border-b">
                  <td className="px-2 py-2">{user.username}</td>
                  <td className="px-2 py-2">{user.info?.name}</td>
                  <td className="px-2 py-2">{user.info?.phoneNumber}</td>
                  <td className="px-2 py-2">{(user.roles ?? []).join(', ')}</td>
                  <td className="px-2 py-2 text-right">
                    <Button
                      onClick={() => openEdit(user)}
                      className="rounded border px-2 py-1 text-12 theme-bg-1"
                    >
                      수정
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <AdminUserEditModal user={editTarget} onSaved={fetchUsers} />
      <AdminUserCreateModal roleOptions={MANAGEABLE_ROLES} onCreated={fetchUsers} />
    </div>
  );
};

export default AdminUserManagePage;
